import React from "react";

import {
    CharBlockOrNull,
    SplitFormBlock as SplitValue,
    FormBlock as Value,
} from "@reactivated";

import { assertNever } from "@thelabnyc/thelabui/src/utils/never";

import { FormType } from "../CRMForm";
import { FormLayout } from "../CRMForm/CRMForm";
import { ConnectWithDealerForm } from "../CRMForm/ConnectWithDealerForm";
import { ContactForm } from "../CRMForm/ContactForm";
import { EventGiveawayForm } from "../CRMForm/EventGiveawayForm";
import { LGBuilderRegistrationForm } from "../CRMForm/LGBuilderRegistrationForm";
import { LocalContactForm } from "../CRMForm/LocalContactForm";
import { RequestAQuoteForm } from "../CRMForm/RequestAQuoteForm";
import { SFGiantsForm } from "../CRMForm/SFGiantsForm";
import { StayInformedForm } from "../CRMForm/StayInformedForm";
import { MediaChooserBlock } from "../MediaChooserBlock";
import { RichText } from "../RichText";
import { Split } from "../SplitBlock";

import styles from "./index.module.scss";

// Adding custom submit urls stresses the interface scheme here so it's being
// omitted for clarity
interface Props extends Omit<FormLayout, "custom_submit_url"> {
    value: Value;
    terms?: CharBlockOrNull;
    success_text?: CharBlockOrNull;
}

export const FormBlock = ({ value, layout, terms, success_text }: Props) => {
    if (!value.form) return <></>;
    switch (value.form) {
        case FormType.REQUEST_A_QUOTE:
            return (
                <RequestAQuoteForm
                    layout={layout ?? "solo"}
                    custom_submit_url={value.custom_submit_url ?? null}
                />
            );
        case FormType.STAY_INFORMED:
            return (
                <StayInformedForm
                    layout={layout ?? "solo"}
                    custom_submit_url={value.custom_submit_url ?? null}
                />
            );
        case FormType.CONTACT:
            return (
                <ContactForm
                    custom_submit_url={value.custom_submit_url ?? null}
                />
            );
        case FormType.LG_BUILDER_REGISTRATION:
            return (
                <LGBuilderRegistrationForm
                    custom_submit_url={value.custom_submit_url ?? null}
                />
            );
        case FormType.CONNECT_WITH_DEALER:
            return (
                <ConnectWithDealerForm
                    custom_submit_url={value.custom_submit_url ?? null}
                />
            );
        case FormType.DESIGN_GUIDE_REQUEST_OPT_IN:
            return <></>;
        case FormType.EVENT_GIVEAWAY:
            return (
                <EventGiveawayForm
                    layout={layout ?? "solo"}
                    terms={terms}
                    success_text={success_text}
                    custom_submit_url={value.custom_submit_url ?? null}
                />
            );
        case FormType.SF_GIANTS:
            return (
                <SFGiantsForm
                    layout={layout ?? "solo"}
                    terms={terms}
                    success_text={success_text}
                    custom_submit_url={value.custom_submit_url ?? null}
                />
            );
        case FormType.LOCAL_REP_CONTACT:
            return (
                <LocalContactForm
                    layout={layout ?? "solo"}
                    custom_submit_url={value.custom_submit_url ?? null}
                />
            );
    }
    assertNever(value.form);
};

export const SplitFormBlock = ({ value }: { value: SplitValue }) => {
    if (!value.form) return <></>;

    return (
        <Split
            contentClassName={styles.tallContent}
            content={
                value.content ? (
                    <div>
                        <RichText value={value.content} />
                        <FormBlock
                            value={{
                                form: value.form,
                                custom_submit_url: value.custom_submit_url,
                            }}
                            terms={value.terms}
                            success_text={value.success_text}
                            layout="full-width"
                        />
                    </div>
                ) : null
            }
            media={value.media && <MediaChooserBlock value={value.media} />}
            contentAlignment={value.content_alignment}
            imageWidth={value.image_width}
            contentAlignPrimary="left/top"
        />
    );
};
